import React, { useMemo, useState } from "react";
import { Box, Paper, Stack, Typography, Button, Grid, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import {
  useListningQuery,
  useCreateStakingPoolMutation,
  useGetMyStakingPoolsQuery,
} from "../../../redux/api/authApi";
import { resolveInvestorUploadUrl } from "../../investor/myInvestmentsMapper";
import ListStakingPoolModal from "./ListStakingPoolModal";
import StakingPoolsSkeleton from "./StakingPoolsSkeleton";

function formatCurrency(num) {
  if (num == null || Number.isNaN(Number(num))) return "—";
  return `SAR ${Number(num).toLocaleString("en-US")}`;
}

const STATUS_CHIP_SX = {
  pending: { bgcolor: "#fff3e0", color: "#ef6c00" },
  approved: { bgcolor: "#dcfce7", color: "#15803d" },
  active: { bgcolor: "#dcfce7", color: "#15803d" },
  rejected: { bgcolor: "#ffebee", color: "#c62828" },
};

function statusChipSx(status) {
  const key = String(status || "").trim().toLowerCase();
  return STATUS_CHIP_SX[key] || { bgcolor: "#f3f4f6", color: "#4b5563" };
}

function categoryIcon(category) {
  const key = String(category || "").trim().toLowerCase();
  if (key === "real estate") return ApartmentOutlinedIcon;
  if (key === "luxury goods") return DiamondOutlinedIcon;
  return LayersOutlinedIcon;
}

export default function StakingPools() {
  const navigate = useNavigate();
  const { data } = useListningQuery();
  const [createStakingPool] = useCreateStakingPoolMutation();
  const {
    data: poolsRes,
    isLoading: poolsLoading,
    error: poolsError,
    refetch: refetchPools,
  } = useGetMyStakingPoolsQuery();
  const [modalOpen, setModalOpen] = useState(false);

  const assetOptions = useMemo(() => {
    const listings = Array.isArray(data?.data) ? data.data : [];
    return listings
      .filter((listing) => String(listing.status || "").trim().toLowerCase() === "active")
      .map((listing) => ({
        id: listing._id,
        name: listing.basicInfo?.assetName || "Unnamed asset",
        imageUrl: resolveInvestorUploadUrl(listing.assetImages?.mainImage?.fileUrl),
      }));
  }, [data?.data]);

  const stakingPools = useMemo(() => {
    const rows = Array.isArray(poolsRes?.data) ? poolsRes.data : [];
    return rows.map((row) => {
      const asset = row.assetId && typeof row.assetId === "object" ? row.assetId : {};
      return {
        id: row._id,
        poolName: row.poolName,
        assetName: asset.basicInfo?.assetName || "Unnamed asset",
        assetImageUrl: resolveInvestorUploadUrl(asset.assetImages?.mainImage?.fileUrl),
        category: row.category || asset.assetCategory?.name || "",
        annualYield: row.annualYield,
        lockPeriod: row.lockPeriod,
        minStake: row.minStake,
        targetStakeAmount: row.targetStakeAmount,
        totalStaked: row.totalStaked,
        participantsCount: row.participantsCount,
        status: row.status,
      };
    });
  }, [poolsRes?.data]);

  const handleCreatePool = async (pool) => {
    await createStakingPool({
      assetId: pool.assetId,
      poolName: pool.poolName,
      annualYield: pool.annualYield,
      lockPeriod: pool.lockPeriod,
      minStake: pool.minStake,
      targetStakeAmount: pool.targetStakeAmount,
    }).unwrap();
  };

  if (poolsLoading) {
    return <StakingPoolsSkeleton />;
  }

  return (
    <Box sx={{ flex: 1, minWidth: 0, px: 2, display: "flex" }}>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Button
          onClick={() => navigate("/asset/dashboard")}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "#111827",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1,
                bgcolor: "background.headerIcon",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SavingsRoundedIcon sx={{ color: "text.slate" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                My Staking Pools
              </Typography>
              <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
                Create and manage staking pools for your tokenized assets.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 3.5,
              py: 1.5,
              fontSize: 14,
              fontWeight: 600,
              boxShadow: (theme) => theme.palette.brand.ctaShadow,
              background: (theme) => theme.palette.brand.ctaGradient,
              "&:hover": {
                boxShadow: (theme) => theme.palette.brand.ctaShadowHover,
                background: (theme) => theme.palette.brand.ctaGradientHover,
              },
            }}
            onClick={() => setModalOpen(true)}
          >
            List Staking Pool
          </Button>
        </Box>

        {poolsError ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="error" sx={{ mb: 2 }}>
              Failed to load staking pools.
            </Typography>
            <Button variant="outlined" onClick={() => refetchPools()}>
              Retry
            </Button>
          </Box>
        ) : stakingPools.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 8 },
              textAlign: "center",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "background.activityIcon",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <LayersOutlinedIcon
                sx={{ fontSize: 34, color: (theme) => theme.palette.stats.indigo }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
              No staking pools yet
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.muted", maxWidth: 420, mx: "auto" }}
            >
              Once you create a staking pool for one of your tokenized assets, it
              will show up here so you can track participation and rewards.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2.5}>
            {stakingPools.map((pool) => {
              const CategoryIcon = categoryIcon(pool.category);
              return (
                <Box
                  key={pool.id}
                  sx={{
                    p: 3,
                    borderRadius: 1.5,
                    bgcolor: (theme) => theme.palette.background.paper,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "flex-start" }}
                    spacing={2}
                    mb={3}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.25,
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CategoryIcon
                          sx={{ color: (theme) => theme.palette.accent.teal.chipText, fontSize: 26 }}
                        />
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>
                            {pool.poolName}
                          </Typography>
                          {pool.category ? (
                            <Chip
                              label={`${pool.category} Staking`}
                              size="small"
                              sx={{
                                bgcolor: (theme) => theme.palette.primary.main,
                                color: "common.white",
                                fontWeight: 600,
                                fontSize: 11,
                                height: 22,
                                borderRadius: 999,
                              }}
                            />
                          ) : null}
                          <Chip
                            label={pool.status}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "capitalize",
                              borderRadius: 999,
                              ...statusChipSx(pool.status),
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" sx={{ color: "text.muted", mt: 0.75 }}>
                          {pool.assetName}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                        Annual Yield
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: (theme) => theme.palette.accent.teal.chipText,
                          letterSpacing: -0.5,
                        }}
                      >
                        {pool.annualYield}%
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                        Lock Period
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                        <LockOutlinedIcon sx={{ fontSize: 14, color: "text.muted" }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
                          {pool.lockPeriod} days
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                        Min Stake
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                        {formatCurrency(pool.minStake)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                        Target Stake Amount
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                        {formatCurrency(pool.targetStakeAmount)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                        Total Staked
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                        {formatCurrency(pool.totalStaked)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                        Participants
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                        <GroupOutlinedIcon sx={{ fontSize: 14, color: "text.muted" }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
                          {pool.participantsCount ?? 0}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <ListStakingPoolModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        assetOptions={assetOptions}
        onSubmit={handleCreatePool}
      />
    </Box>
  );
}
